import React from "react";
import Link from "next/link";

import { routes } from "src/shared/routes";

import type { TTeamConfig } from "src/shared/types/teamConfig.types";
import type { TRegistrationCardData } from "src/shared/types/globalStotre.types";

export const REGISTRATION_CARDS: TRegistrationCardData[] = [
  {
    title: "Oregon Student Tickets",
    description: (
      <>
        Student tickets for the 2026-27 Athletic Season are available here -
        login to claim your free student ticket to upcoming games!
        <br />
        <br />
        More information about the 2026-27 student football ticket claim process
        can be found{" "}
        <Link
          href="https://goducks.com/sports/2020/6/15/student-tickets"
          target="_blank"
        >
          here
        </Link>
        .
      </>
    ),
    authRedirectPage: routes.home,
    buttonLabel: "View Available Tickets",
  },
  {
    title: "Student Marketplace",
    description:
      "The official platform for students to buy and sell football tickets to the student section. If you can't make it to a game, list your ticket here for another student to purchase. Looking for a ticket? Browse listings from your fellow classmates and secure your spot in the student section. This is the only approved place to sell your ticket without risking your season ticket privileges.",
    authRedirectPage: routes.marketplace_home,
    buttonLabel: "Go to Marketplace",
    buttonVariant: "contained",
  },
];

export const FAQS: TTeamConfig["LANGUAGES"]["FAQS"] = [
  {
    question: "Am I eligible for student tickets?",
    answer:
      "To be eligible to purchase/claim a fall sport (football, volleyball, soccer) student ticket, you have to be currently enrolled and registered for the fall term at the University of Oregon. Winter and Spring sport eligibility is determined by current term enrollment/registration.",
  },
  {
    question: "How are tickets sent to me?",
    answer:
      "You will be notified via text message when barcodes are available in your GoDucks App. We recommend saving tickets to your Apple Wallet or G Pay to avoid connectivity at the gates.",
  },
  {
    question: "Where will my seats be located? ",
    answer: "Seats are located in the student section for all events.",
  },
  {
    question: "Can these tickets be transferred to a friend?",
    answer: "Student tickets are not eligible for re-sale or transfer.",
  },
  {
    question: "Can my seats be changed?",
    answer: (
      <>
        If you have questions about your seat location, please contact{" "}
        <Link href="tel:5413464461">(541) 346-4461</Link> or{" "}
        <Link href="mailto:tickets@uoregon.edu">tickets@uoregon.edu</Link>.
      </>
    ),
  },
  {
    question: "Can I bring a friend to a game?",
    answer:
      "If additional inventory for guest tickets is available, you will be notified via email regarding any additional claim timing.",
  },
  {
    question: "Can I receive a refund if I cannot attend a game?",
    answer: "We do not issue any refunds for games you are not able to attend.",
  },
  {
    question: "How can I get a refund and cancel my pass?",
    answer: (
      <>
        For any questions regarding your Duck Sports Pass cancellation, please
        contact <Link href="tel:5413464461">(541) 346-4461</Link> or{" "}
        <Link href="mailto:tickets@uoregon.edu">tickets@uoregon.edu</Link>.
      </>
    ),
  },
];

export const TERMS_AND_CONDITION = () => (
  <>
    By purchasing you agree to receive SMS alerts regarding ticket delivery and
    agree to the{" "}
    <Link target="_blank" href="https://www.attend.tech/terms-of-use">
      Terms of Use
    </Link>{" "}
    and{" "}
    <Link target="_blank" href="https://www.attend.tech/privacy-policy">
      Privacy Policy
    </Link>
    .
  </>
);

export const FOOTER_LINKS = () => (
  <>
    <Link
      target="_blank"
      href="https://www.attend.tech/terms-of-use"
      style={{ color: "white " }}
    >
      Terms of Use
    </Link>{" "}
    <Link
      target="_blank"
      href="https://www.attend.tech/privacy-policy"
      style={{ color: "white " }}
    >
      Privacy Policy
    </Link>
  </>
);

export const ADD_ON_PURCHASE_SUCCESS_MESSAGE = {
  TITLE: () => <>You&apos;re all set to experience the action!</>,
  DESCRIPTION: () => {
    return (
      <>
        View your tickets{" "}
        <b>
          <u>
            <Link
              target="_blank"
              style={{ color: "white" }}
              href="https://am.ticketmaster.com/oregonducks/"
            >
              here
            </Link>
          </u>
        </b>
        .
      </>
    );
  },
};

export const ListingCheckoutTermsAndCondition = () => {
  return (
    <>
      By checking this box you have read and agree to Attend&apos;s{" "}
      <Link
        className="global-link"
        target="_blank"
        href="https://www.attend.tech/terms-of-use"
      >
        Terms of Use
      </Link>
      ,{" "}
      <Link
        className="global-link"
        target="_blank"
        href="https://www.attend.tech/privacy-policy"
      >
        Privacy Policy
      </Link>{" "}
      and{" "}
      <Link
        className="global-link"
        target="_blank"
        href="http://attend.tech/marketplace-purchase-policy "
      >
        Purchase Policy
      </Link>
      .
    </>
  );
};

export const CreateListingTermsAndCondition = () => {
  return (
    <>
      By clicking &quot;Create Listing&quot;, you have read and agree to
      Attend&apos;s{" "}
      <Link
        className="global-link"
        target="_blank"
        href="https://www.attend.tech/terms-of-use"
      >
        Terms of Use
      </Link>
      ,{" "}
      <Link
        className="global-link"
        target="_blank"
        href="https://www.attend.tech/privacy-policy"
      >
        Privacy Policy
      </Link>{" "}
      and{" "}
      <Link
        className="global-link"
        target="_blank"
        href="http://attend.tech/marketplace-purchase-policy "
      >
        Purchase Policy
      </Link>
      .
    </>
  );
};

const STUDENT_TICKET_LINKS: {
  label: string;
  href: string;
}[] = [
  {
    label: "Mobile Ticket Information",
    href: "https://goducks.com/sports/2020/5/18/mobile-ticketing",
  },
  {
    label: "Student Ticket Information",
    href: "https://goducks.com/sports/2020/6/15/student-tickets",
  },
  {
    label: "Student Football Ticket FAQ Page",
    href: "https://goducks.com/sports/2021/9/1/student-tickets-faq",
  },
];

/** The three goducks.com reference links repeated at the bottom of both
 *  marketplace blue bars. */
function StudentTicketLinks() {
  return (
    <>
      {STUDENT_TICKET_LINKS.map((link) => (
        <React.Fragment key={link.href}>
          <Link
            href={link.href}
            target="_blank"
            style={{ textDecoration: "underline" }}
          >
            {link.label}
          </Link>
          <br />
        </React.Fragment>
      ))}
    </>
  );
}

/** Blue bar on the buyer-side Available Events page. */
export const MARKETPLACE_EVENTS_PAGE_INFO_TEXT = (
  <>
    Click on a game to browse the available student football tickets. Listings
    for each game will close two days prior to the game at 12 PM PST (Thursday
    for a Saturday game). After purchasing another student&apos;s ticket, your
    ticket will be delivered in the GoDucks Gameday App the Friday before
    gameday. Students should enter Autzen Stadium via the Student Section Gates
    on the South Side of the Stadium.
    <br />
    <br />
    Student tickets are limited to 1 ticket per student. Mobile Ticket and
    Student ID are required to receive a wristband for entry. Wristbands are
    only distributed at the student gate. Students found using another
    student&apos;s ID will be reported to the Office of Student Conduct and
    Community Standards for misuse of a university ID. The only exception to
    this is if Student Friends and Family tickets are sold for the game. Friends
    and Family tickets may be used by non-UO Students.
    <br />
    <br />
    <StudentTicketLinks />
  </>
);

/** Blue bar on the seller-side Available Events / create listing page. */
export const MARKETPLACE_CREATE_LISTING_PAGE_INFO_TEXT = (
  <>
    List your student tickets for upcoming games and check back here to track
    their status — only student tickets can be listed. Listings for each game
    will close two days prior to the game at 12 PM PST (Thursday for a Saturday
    game), at which point any unsold tickets will be removed and you will
    receive your ticket with seat locations the Friday before gameday.
    <br />
    <br />
    <StudentTicketLinks />
  </>
);

export const BUYER_HOW_IT_WORKS = [
  {
    title: 'Step 1: Select "Browse Listings"',
  },
  {
    title:
      "Step 2: Log in with the email address associated with your Oregon Ducks Account Manager.",
  },
  {
    title: "Step 3: Select the game you are interested in attending.",
  },
  {
    title: "Step 4: Select a listing you wish to purchase.",
  },
  {
    title: "Step 5: Complete the purchase of your ticket.",
  },
  {
    description: (
      <>
        You will be notified via text when your tickets for the game are
        available to be managed in the GoDucks Gameday App on the Friday before
        gameday.
        <br />
        <br />
        All sales are final. No refunds will be issued.
      </>
    ),
  },
];

export const SELLER_HOW_IT_WORKS = [
  {
    title: 'Step 1: Select "Sell Your Ticket"',
  },
  {
    title:
      "Step 2: Log in with the email address associated with your Oregon Ducks Account Manager.",
  },
  {
    title: "Step 3: Select the game you wish to sell.",
  },
  {
    title: "Step 4: Complete the Seller Onboarding process.",
  },
  {
    title: "Step 5: Enter the number of tickets you wish to list.",
  },
  {
    title: (
      <>
        Step 6: Set your listing price for the tickets and click &quot;List for
        Resale&quot;.
        <br />
        <br />
      </>
    ),
  },

  {
    title: "Congrats! Your listing is complete",
    description: (
      <>
        If your ticket is purchased, you will be notified via text. Check back
        in on the portal at any time to view or update the status of your
        listing.
        <br />
        <br />
        If plans change and you can attend the game, be sure to remove your
        listing from the marketplace. If your ticket is purchased, no refunds
        will be issued.
        <br />
        <br />
        Please wait 5 business days for payouts to reflect in your bank
        statement prior to reaching out to support by clicking on Chat with
        Support in the right hand hamburger menu.
        <br />
        <br />
        All sales are final.
        <br />
        <br />
        Unsold tickets will be removed from the Marketplace. If your ticket does
        not sell, you will receive your seat location and tickets Friday before
        gameday.
      </>
    ),
  },
];

export function MARKETPLACE_FAQS(teamSchema: string) {
  return [
    {
      question: "What is the Student Marketplace?",
      answer: (
        <>
          The Student Marketplace is the The Student Marketplace is the{" "}
          <b>only official, university-approved</b> platform for Oregon Ducks
          students to securely <b> buy and sell student football tickets</b>. It
          ensures that tickets stay within the student community and helps
          protect your ticket privileges.
        </>
      ),
    },
    {
      question: "Who can use the Student Marketplace?",
      answer: (
        <>
          Only <b>Oregon students</b> can access the platform using the email
          associated with your Oregon Ducks Account Manager.
        </>
      ),
    },
    {
      question: "Can I sell my student ticket elsewhere?",
      answer: (
        <>
          No. Selling your student ticket on any other platform is not allowed
          and may result in the <b>loss of your student ticket</b>.
        </>
      ),
    },
    {
      question: "When can I list my ticket?",
      answer: (
        <>
          Listings can be created at any time,{" "}
          <b>
            but all sales must be finalized by 12:00 PM on the Wednesday before
            gameday.
          </b>
        </>
      ),
    },
    {
      question: "What if I change my mind after listing my ticket?",
      answer: (
        <>
          You can remove your ticket from the Marketplace at any time{" "}
          <b>before it is purchased</b>. If your ticket is purchased, the sale
          is <b>final</b> and <b>cannot be reversed or refunded</b>, even if
          your plans change.
        </>
      ),
    },
    {
      question: "When will I receive my ticket(s)?",
      answer: (
        <>
          Tickets will be available to manage on the Friday before gameday, and
          you will be notified via text. You will receive your seat location and
          digital ticket at that time.
        </>
      ),
    },
    {
      question: "When will I receive my payout?",
      answer: (
        <>
          If your ticket is sold, your payout is triggered immediately, but
          please allow 5 business days for it to reflect on your bank statement.
        </>
      ),
    },
    {
      question: "Can I sit with friends if I buy a ticket?",
      answer: (
        <>
          Yes. After purchasing a ticket, navigate to the{" "}
          <Link
            className="global-link"
            href={{
              pathname: routes.my_group,
              query: { teamSchema },
            }}
          >
            My Groups
          </Link>{" "}
          section and <b>create or join</b> a group to be seated with your
          friends. Group submissions must be finalized by{" "}
          <b>12:00 PM on the Wednesday before gameday.</b>
        </>
      ),
    },
    {
      question: "Are refunds available?",
      answer: (
        <>
          No. <b>All sales are final</b> for both buyers and sellers. Please
          make sure of your plans before buying or listing a ticket.
        </>
      ),
    },
    {
      question: "How does My Groups work?",
      answer: (
        <>
          The{" "}
          <Link
            className="global-link"
            href={{
              pathname: routes.my_group,
              query: { teamSchema },
            }}
          >
            My Groups
          </Link>{" "}
          feature is open continuously, allowing you to create or join a group
          at any time. However,{" "}
          <b>
            group formation locks at 12:00 PM EST on the Wednesday before
            gameday
          </b>{" "}
          for that week. <br />
          <br />
          If you create or update a group <b>after Wednesday at 12:00 PM EST</b>
          , it will <b>only apply to the following game</b>, not the upcoming
          one. Be sure to finalize your group before the deadline to be seated
          with your friends.
        </>
      ),
    },
    {
      question: "How do I contact support?",
      answer: (
        <>
          If you need help, click on <b>Chat with Support</b> in the menu icon
          (three line logo) at the top-right corner.
          <br />
          <br />
          Support is available{" "}
          <b>Monday through Friday, 9:00 AM to 5:00 PM EST</b>, and during
          <b>gamedays</b> to assist with urgent questions.
        </>
      ),
    },
  ];
}
